/**
 * 保留名单：用户不得把角色名或创作者名设为自己的称谓。
 *
 * **这是管理端第一个写页面。** 它立的规矩后续照抄：
 *   - 每个写操作弹框要求填「变更理由」，直接进 AuditLog.reason
 *   - 成功后 invalidate 对应 queryKey，不手工改本地状态
 *   - 危险操作（删除）二次确认
 */

import { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../services/api";
import { fmtDateTime } from "../../components/format";

interface ReservedNameRow {
  id: string;
  value: string;
  matchMode: "exact" | "contains";
  note: string | null;
  scope: "global" | "character";
  characterId: string | null;
  characterName: string | null;
  createdByName: string | null;
  createdAt: string | null;
}

interface CharacterOption {
  id: string;
  displayName: string;
}

const MATCH_MODE_LABEL: Record<string, string> = {
  exact: "完全相同",
  contains: "包含即拒",
};

export function ReservedNamesPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [form] = Form.useForm();

  const characters = useQuery({
    queryKey: ["characters"],
    queryFn: () => api.get<CharacterOption[]>("/api/v1/admin/characters"),
  });

  const list = useQuery({
    queryKey: ["reserved-names"],
    queryFn: () => api.get<{ items: ReservedNameRow[]; total: number }>("/api/v1/admin/reserved-names", {
      pageSize: 200,
    }),
  });

  const create = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post("/api/v1/admin/reserved-names", values),
    onSuccess: () => {
      message.success("已加入保留名单");
      setAddOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ["reserved-names"] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.del(`/api/v1/admin/reserved-names/${id}`, { reason }),
    onSuccess: () => {
      message.success("已移出保留名单");
      void qc.invalidateQueries({ queryKey: ["reserved-names"] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (list.error) return <Alert type="error" showIcon message={(list.error as Error).message} />;

  return (
    <>
      <Card
        styles={{ body: { padding: 12 } }}
        extra={
          <Button type="primary" onClick={() => setAddOpen(true)}>
            添加保留名
          </Button>
        }
      >
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
          用户不得把这些词设为自己的<strong>称谓</strong>（<code>RelationshipState.userPreferredName</code>）。
          起因是旧库某条关系的称谓被抽成了「司大妈娲大妈」，缈缈于是管用户叫「大妈」；
          另有 21 条消息直接把用户称作创作者的名字，一边否认「我不是司娲」一边喊对方「司娲」。
          用户纠正了 11 次。
          <br />
          匹配前会先归一化：去首尾空白、全角转半角、去零宽字符、转小写。
          <strong>「包含即拒」会误伤</strong>——例如「笨笨」若用包含匹配会连真实用户的「小笨笨」一起挡掉，
          所以它设成了完全匹配。改动后请跑 <code>tools/verify-reserved-names.ts</code> 回归。
        </Typography.Paragraph>

        <Table<ReservedNameRow>
          rowKey="id"
          size="small"
          loading={list.isLoading}
          dataSource={list.data?.items ?? []}
          pagination={false}
          columns={[
            { title: "词", dataIndex: "value", width: 160, render: (v) => <strong>{v}</strong> },
            {
              title: "匹配方式",
              dataIndex: "matchMode",
              width: 110,
              render: (v: string) => (
                <Tag color={v === "contains" ? "orange" : "default"}>{MATCH_MODE_LABEL[v] ?? v}</Tag>
              ),
            },
            {
              title: "生效范围",
              dataIndex: "scope",
              width: 130,
              render: (v: string, r) =>
                v === "global" ? <Tag color="red">全部角色</Tag> : <Tag>{r.characterName}</Tag>,
            },
            { title: "原因", dataIndex: "note", render: (v) => v || "—" },
            { title: "添加人", dataIndex: "createdByName", width: 110, render: (v) => v || "—" },
            { title: "添加于", dataIndex: "createdAt", width: 170, render: fmtDateTime },
            {
              title: "操作",
              width: 80,
              render: (_, r) => <RemoveButton row={r} onConfirm={(reason) => remove.mutate({ id: r.id, reason })} />,
            },
          ]}
        />
      </Card>

      <CheckCard characters={characters.data ?? []} />

      <Modal
        title="添加保留名"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={create.isPending}
        okText="添加"
      >
        <Form form={form} layout="vertical" onFinish={(v) => create.mutate(v)} initialValues={{ matchMode: "contains" }}>
          <Form.Item
            name="value"
            label="词"
            rules={[{ required: true, message: "必填" }, { max: 64 }]}
            extra="会自动归一化后保存"
          >
            <Input placeholder="如 缈缈" />
          </Form.Item>
          <Form.Item name="matchMode" label="匹配方式" rules={[{ required: true }]}>
            <Select
              options={[
                { value: "contains", label: "包含即拒 — 用于专有名词，防「缈缈酱」这类加缀绕过" },
                { value: "exact", label: "完全相同 — 用于常见词，避免误伤" },
              ]}
            />
          </Form.Item>
          <Form.Item name="characterId" label="生效范围" extra="留空表示对所有角色生效，创作者名属于这一类">
            <Select
              allowClear
              placeholder="全部角色"
              options={(characters.data ?? []).map((c) => ({ value: c.id, label: c.displayName }))}
            />
          </Form.Item>
          <Form.Item name="note" label="原因" extra="会展示给运营，也用于拒绝时的提示文案">
            <Input placeholder="如 创作者名" />
          </Form.Item>
          <Form.Item name="reason" label="变更理由" rules={[{ required: true, message: "写操作必须填理由，会进审计日志" }]}>
            <Input.TextArea rows={2} placeholder="为什么加这个词" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

function RemoveButton({ row, onConfirm }: { row: ReservedNameRow; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Popconfirm
      title={`移出保留名单：${row.value}`}
      description={
        <Space direction="vertical" style={{ width: 260 }}>
          <span style={{ fontSize: 12 }}>移出后用户就能把它设为称谓了。</span>
          <Input.TextArea
            rows={2}
            placeholder="变更理由（必填，进审计）"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Space>
      }
      okButtonProps={{ danger: true, disabled: reason.trim().length === 0 }}
      okText="移除"
      onConfirm={() => onConfirm(reason.trim())}
    >
      <Button danger size="small" type="link">
        移除
      </Button>
    </Popconfirm>
  );
}

/** 试算面板：输入一个称谓看会不会被拒。纯读，不产生写入。 */
function CheckCard({ characters }: { characters: CharacterOption[] }) {
  const [characterId, setCharacterId] = useState<string | undefined>();
  const [value, setValue] = useState("");
  const [result, setResult] = useState<{ allowed: boolean; hit: { note: string | null } | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const effectiveCharacter = characterId ?? characters[0]?.id;

  async function run() {
    if (!effectiveCharacter || !value.trim()) return;
    setBusy(true);
    try {
      const r = await api.get<{ allowed: boolean; hit: { note: string | null } | null }>(
        "/api/v1/admin/reserved-names/check",
        { characterId: effectiveCharacter, value: value.trim() },
      );
      setResult(r);
    } catch (e) {
      message.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card size="small" title="试算" style={{ marginTop: 12 }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        改动名单后用真实称谓验一下，确认没误伤。
      </Typography.Paragraph>
      <Space wrap>
        <Select
          style={{ width: 140 }}
          placeholder="角色"
          value={effectiveCharacter}
          onChange={setCharacterId}
          options={characters.map((c) => ({ value: c.id, label: c.displayName }))}
        />
        <Input
          style={{ width: 240 }}
          placeholder="输入一个称谓，如 小笨笨"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setResult(null);
          }}
          onPressEnter={run}
        />
        <Button onClick={run} loading={busy}>
          试算
        </Button>
        {result &&
          (result.allowed ? (
            <Tag color="green">放行</Tag>
          ) : (
            <Tag color="red">拒绝{result.hit?.note ? ` · ${result.hit.note}` : ""}</Tag>
          ))}
      </Space>
    </Card>
  );
}
